'use client';

import { Compass } from '@shared/icons';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import { useGamesList } from '../../casino/context/GamesListProvider/GamesListProvider';
import { areGameProvidersEqual } from '../../filters/getAvailableGameProviders';
import { NoResults } from '../../NoResults/NoResults';
import { ClassicGameList } from '../ClassicGameList/ClassicGameList';
import type { GamesCategoryFilter } from '../types/types';
import { useFavouritesRemoval } from './hooks/useFavouritesRemoval';
import { useGameFiltering } from './hooks/useGameFiltering';
import { useGamesFromUrl } from './hooks/useGamesFromUrl';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import { LayoutSkeleton } from './LayoutSkeleton';
import { createCardClasses, createContainerClasses } from './styles';

type FilteredGamesListProps = {
  filters?: GamesCategoryFilter;
  isFavourites?: boolean;
  isCasinoProductVertical?: boolean;
  className?: string;
  isLoggedIn?: boolean;
};

const containerClasses = createContainerClasses();
const cardClasses = createCardClasses();

function FilteredGamesList({ filters, isFavourites = false, isCasinoProductVertical = false, isLoggedIn = false, className }: FilteredGamesListProps) {
  const t = useTranslations('casino.filters.not_found');

  const {
    isLayoutTransitioning,
    layout,
    updateGamesCounter,
    hasProvidersFiltersApplied,
    updateIsGamesLoading,
    availableGameProviders: currentGameProviders,
    updateAvailableGameProviders,
  } = useGamesList();

  const { category } = filters ?? {};

  const { filterGames, availableGameProviders, isLoading } = useGameFiltering(filters, isLoggedIn, isFavourites, isCasinoProductVertical);
  const { games, setGames, isFilteringGames, hasInitialized, searchParamsKey } = useGamesFromUrl({
    filterGames,
    isLoading,
    category,
  });
  const { sentinelRef, visibleCount } = useInfiniteScroll({
    totalCount: games.length,
    searchParamsKey,
  });
  const { onRemoveGameRef } = useFavouritesRemoval({ setGames });

  useEffect(() => {
    updateGamesCounter(games.length);
  }, [games.length, updateGamesCounter]);

  useEffect(() => {
    updateAvailableGameProviders(areGameProvidersEqual(currentGameProviders, availableGameProviders) ? currentGameProviders : availableGameProviders);
  }, [availableGameProviders, currentGameProviders, updateAvailableGameProviders]);

  const isGamesLoading = isLoading || isFilteringGames || !hasInitialized;

  useEffect(() => {
    updateIsGamesLoading(isGamesLoading);
  }, [isGamesLoading, updateIsGamesLoading]);

  const visibleGames = useMemo(() => games.slice(0, visibleCount), [games, visibleCount]);

  if (isLoading || isFilteringGames || !hasInitialized) {
    return (
      <div className={className}>
        <LayoutSkeleton layoutVariant={layout} />
      </div>
    );
  }

  if (games.length === 0 && !(isFavourites && !hasProvidersFiltersApplied)) {
    return <NoResults title={t('title')} description={t('description')} icon={Compass} className={className} />;
  }

  return (
    <div className={className}>
      <div className={containerClasses({ variant: layout, transitioning: isLayoutTransitioning })} data-testid="games-results-container" data-track-container="Results">
        <ClassicGameList
          games={visibleGames}
          variant={layout}
          namespace="casino"
          cardClassName={cardClasses({ variant: layout })}
          onRemoveGameRef={isFavourites ? onRemoveGameRef : undefined}
          baseUrl={isCasinoProductVertical ? ROUTES.CASINO : ROUTES.LIVE_CASINO}
        />
      </div>
      <div ref={sentinelRef} aria-hidden="true" />
    </div>
  );
}

export { FilteredGamesList };
